import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toastr } from 'react-redux-toastr';
import styled, { withTheme } from 'styled-components';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import InputField_T from 'common/components/atoms/InputField';
import FlexContainer from 'common/components/atoms/FlexContainer';
import AlertPrompt from 'common/components/organisms/AlertPrompt';
import { deleteAlarmPrefixes } from 'home/AssetHierarchy/utils/alarmParameters';

import * as assetDetailsActions from '../../../actions/assetDetails.actions';
import * as envelopeActions from '../../../actions/envelope.actions';
import { DELETE_OVERALL_ALARM_OPTIONS } from '../../../constants/overallAlarm.constants';

const OverallAlarmsOptionsContainer = styled(FlexContainer)`
  border: 1px solid grey;
  border-spacing: 15px;
  border-color:${props => props.theme.colors.lightGray};
  border-radius: 8px;
  padding: 20px;
  width: 50%;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const InputField = styled(InputField_T)`
  flex-basis: 50%
  margin-bottom: 0.25em;
`;


class DeleteOverallAlarmModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      params: this.getInitialParams()
    };
  }

  getOverallAlarmDeleteMessage = () => {
    const { assetInfo: { node_name } } = this.props;
    return this.props.t('assetHierarchy.deleteOverallAlarms.message', {
      defaultValue: 'Please select the overall alarms of {{name}} which you want to delete:',
      name: node_name
    });
  }

  getInitialParams = () => DELETE_OVERALL_ALARM_OPTIONS.reduce(
    (params, option) => ({ ...params, [option.value]: true }), {}
  )

  toggleParamsOption = (param) => {
    this.setState(prevState => ({
      params: {
        ...prevState.params,
        [param]: !prevState.params[param]
      }
    }));
  }

  renderDeleteOptionsField = () => {
    const { params } = this.state;
    return (
      <OverallAlarmsOptionsContainer>
        {DELETE_OVERALL_ALARM_OPTIONS.map(option => (
          <InputField
            type="checkbox"
            value={params[option.value]}
            title={this.props.t(option.tKey, { defaultValue: option.defaultValue })}
            onClick={() => this.toggleParamsOption(option.value)}
          />
        ))}
      </OverallAlarmsOptionsContainer>
    );
  }

  deleteTagAlarms = (tag) => {
    const { params } = this.state;

    let overallAlarmThresholds = _.cloneDeep(tag.alarm_thresholds);
    let overallAlarmSettings = _.cloneDeep(tag.overall_alarm_settings);
    if (tag.type === 'vibration') {
      const thresholdsKeys = ['critical', 'warning'];
      const settingsKeys = ['base_graph', 'threshold', 'lower_outlier_limit'];
      const prefixes = deleteAlarmPrefixes(tag, params);

      _.forEach(overallAlarmSettings, (value, bin) => {
        _.forEach(settingsKeys, (key) => {
          _.forEach(prefixes, (prefix) => {
            delete overallAlarmSettings[bin][`${prefix}${key}`];
          });
        });
        if (_.isEmpty(overallAlarmSettings[bin])) delete overallAlarmSettings[bin];
      }
      );
      _.forEach(overallAlarmThresholds, (value, bin) => {
        _.forEach(thresholdsKeys, (key) => {
          _.forEach(prefixes, (prefix) => {
            delete overallAlarmThresholds[bin][`${prefix}${key}`];
          });
        });
        if (_.isEmpty(overallAlarmThresholds[bin])) delete overallAlarmThresholds[bin];
      }
      );
    } else if (tag.type === 'temperature') {
      if (params.temperature) {
        overallAlarmThresholds = null;
        overallAlarmSettings = null;
      }
    } else if (params.others) {
      overallAlarmThresholds = null;
      overallAlarmSettings = null;
    }

    const updatedTag = {
      id: tag.id,
      alarm_thresholds: !_.isEmpty(overallAlarmThresholds) ? overallAlarmThresholds : null,
      overall_alarm_settings: !_.isEmpty(overallAlarmSettings) ? overallAlarmSettings : null
    };
    return updatedTag;
  }

  onDeleteOverallAlarms = () => {
    const { site, closeModal } = this.props;
    const node_id = this.props.assetInfo.id;
    this.props.assetDetailsActions.getTags(node_id, site.id, false).then(
      (res) => {
        const tags = _.map(res, t => (this.deleteTagAlarms(t)));
        this.props.envelopeActions.bulkUpdateTags(node_id, { tags }).then(
          (res) => {
            toastr.success(res.message);
            this.props.assetDetailsActions.getTags(node_id, site.id);
            closeModal();
          },
          (error) => {
            toastr.error(error.message);
          }
        );
      }
    );
  }

  isProceedDisabled = () => !_.some(this.state.params);

  render() {
    const { closeModal, theme } = this.props;
    return (
      <AlertPrompt
        message={this.getOverallAlarmDeleteMessage()}
        secondaryMessage={this.props.t('common.actionCannotBeUndone', { defaultValue: 'This action can not be undone' })}
        onCancel={closeModal}
        onProceedDisabled={this.isProceedDisabled()}
        onProceed={this.onDeleteOverallAlarms}
        component={this.renderDeleteOptionsField()}
        proceedColor={theme.primaryColor}
      />
    );
  }
}

DeleteOverallAlarmModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  assetInfo: PropTypes.object.isRequired,
  site: PropTypes.object.isRequired
};

const mapDispatchToProps = dispatch => ({
  assetDetailsActions: bindActionCreators(assetDetailsActions, dispatch),
  envelopeActions: bindActionCreators(envelopeActions, dispatch)
});

export default connect(
  null,
  mapDispatchToProps
)(withTranslation()(withTheme(DeleteOverallAlarmModal)));
